// Unpublished Work © 2023-2024 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {connect} from 'react-redux';
import MediaQuery, {MOBILE_MEDIA_QUERY} from 'Ui/components/higher-order-components/media-query';
import Stack from '@mui/material/Stack';
import {Button, DropdownMenu, IconButton} from '@deere/isg.component-library';
import {isAuthorized} from 'Common/utils/authorization-handler';
import {IconMore} from '@deere/icons';

function ActionButtons(props) {
    const {
        isMobile,
        items,
        isMigrated,
        myJdPermissions,
        translations,
        ...otherProps
    } = props;

    if (items.length === 0) {
        return null;
    }

    const filteredItems = items.filter((item) => !item.hidden && isAuthorized({
        myJdPermissions: item.requiredMyJdPermissions
    }, {
        myJdPermissions,
        isMigrated
    }));

    if (filteredItems.length === 0) {
        return null;
    }

    function renderItem(item) {
        const {
            Icon,
            title,
            asDropdown,
            dropdownProps,
            dataId = `id-${title}`,
            ...buttonProps
        } = item;

        const {[title]: translatedTitle} = translations;

        if (Icon && !translatedTitle) {
            return (
                <IconButton
                    {...buttonProps}
                    data-id={dataId}
                    key={title}
                >
                    {Icon}
                </IconButton>
            );
        }
        if (asDropdown && dropdownProps) {
            const titleItems = dropdownProps.items.map((item) => ({
                ...item,
                title: translations[item.title]
            }));

            delete dropdownProps.items;
            return (
                <DropdownMenu
                    buttonIcon={Icon}
                    buttonTitle={translatedTitle}
                    data-id={dataId}
                    items={titleItems}
                    sx={{
                        'span:nth-child(3)': {
                            display: 'none'
                        }
                    }}
                    {...dropdownProps}
                />
            );
        }
        return (
            <Button
                data-id={dataId}
                {...buttonProps}
                key={title}
                startIcon={Icon}
                sx={{
                    padding: '5px'
                }}
            >
                {translatedTitle}
            </Button>
        );
    }

    return isMobile && filteredItems.length > 1 ? (
        <DropdownMenu
            buttonIcon={<IconMore/>}
            buttonTitle={translations.ACTIONS}
            items={filteredItems.map((item) => ({
                ...item,
                title: translations[item.title]
            }))}
            {...otherProps}
        />
    ) : (
        <Stack
            data-testid='action-buttons'
            direction='row'
            spacing={2}
            {...otherProps}
        >
            {
                filteredItems.map((item) => renderItem(item))
            }
        </Stack>
    );
}

ActionButtons.propTypes = {
    isMigrated: PropTypes.bool,
    isMobile: PropTypes.bool,
    items: PropTypes.arrayOf(PropTypes.object),
    myJdPermissions: PropTypes.myJdPermissions,
    requiredMyJdPermissions: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
    translations: PropTypes.translations
};

export function mapStateToProps(state) {
    return {
        items: state.tertiaryNavBar.actions,
        isMigrated: state.membership.isMigrated,
        myJdPermissions: state.account.myJdPermissions
    };
}

export function mergeProps(stateProps, dispatchProps, ownProps) {
    return {
        ...ownProps,
        ...stateProps
    };
}

export default connect(mapStateToProps, null, mergeProps)(MediaQuery(MOBILE_MEDIA_QUERY)(ActionButtons));
